import * as React from 'react';

import type { IMessage } from 'widget/types/messages';

import useScrollToBottom from './useScrollToBottom';
import useUnreadMessages from './useUnreadMessages';

type UseChatScrollResult = {
  unreadCount: number;
  showScrollButton: boolean;
  handleScrollToBottom: () => void;
};

/**
 * Композитный хук для управления скроллом в чате.
 * Объединяет отслеживание позиции скролла и непрочитанных сообщений.
 *
 * Логика автоскролла:
 * - Первая загрузка — всегда скроллим к последнему сообщению
 * - Новое сообщение от пользователя — всегда скроллим (он только что отправил)
 * - Новое сообщение от бота — скроллим только если пользователь был внизу
 */
const useChatScroll = (
  containerRef: React.RefObject<HTMLElement>,
  messages: IMessage[]
): UseChatScrollResult => {
  const { isNearBottom, scrollToBottom } = useScrollToBottom(containerRef);
  const { unreadCount, resetUnread } = useUnreadMessages(
    messages,
    isNearBottom
  );

  const prevLastMessageIdRef = React.useRef<string | null>(null);

  React.useLayoutEffect(() => {
    if (messages.length === 0) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const lastMessageId = lastMessage?.id ?? null;
    const isFirstLoad = prevLastMessageIdRef.current === null;
    const hasNewMessageAtEnd = lastMessageId !== prevLastMessageIdRef.current;

    const shouldScroll =
      isFirstLoad ||
      (hasNewMessageAtEnd && (!lastMessage.isBot || isNearBottom));

    if (shouldScroll) {
      requestAnimationFrame(() => {
        scrollToBottom(false);
      });
    }

    prevLastMessageIdRef.current = lastMessageId;
  }, [messages, scrollToBottom, isNearBottom]);

  const handleScrollToBottom = React.useCallback((): void => {
    scrollToBottom(true);
    resetUnread();
  }, [scrollToBottom, resetUnread]);

  return {
    unreadCount,
    showScrollButton: !isNearBottom,
    handleScrollToBottom
  };
};

export default useChatScroll;
